import { toastError } from '@onestep/coreui/Toast';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import queryString from 'query-string';
import { useEffect, useMemo } from 'react';
import { Cookies } from 'react-cookie';
import { isMobile, isTablet } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';

import { DEFAULT_NS } from '@/i18n/types';
import {
  BRIDGE_PARAM_CODE_CHALLENGE,
  BRIDGE_PARAM_REDIRECT_URI,
  BRIDGE_PARAM_STATE,
  clinicianBridgeParamsStorageKey,
  type IClinicianBridgeParams,
  LOGIN_REFUSED_ERROR,
  mobileLoginCookieKey,
  resumeSessionAtReturnTo,
  storeClinicianBridgeParams,
  useLoginService,
} from '@/services/loginService';

import BoxesIllustration from '../../assets/images/Login/Boxes.svg?url';
import { LoadingPlaceholder } from '../../components/LoadingPlaceholder/index';
import useQueryParam from '../../hooks/useCustomQueryParam';
import useIsPrimaryTheme from '../../hooks/useIsPrimaryTheme';
import useLoggedUser from '../../hooks/useLoggedUser';
import useMixpanel from '../../hooks/useMixpanel';
import MixpanelProvider from '../../hooks/useMixpanel/MixpanelProvider';
import Challenges from '../Challenges';
import WelcomeScreen from '../SignUp/WelcomeScreen';
import ForgotPasswordContent from './ForgotPasswordContent';
import BackButton from './ForgotPasswordContent/BackButton';
import styles from './index.module.scss';
import LoginContent from './LoginContent';
import { LoginHeader } from './LoginHeader';
import LoginWithSsoContent from './LoginWithSsoContent';

export interface IMfaParams {
  CODE_DELIVERY_DELIVERY_MEDIUM: 'sms' | 'email';
  CODE_DELIVERY_DESTINATION: string;
}

const cookies = new Cookies();

function Login() {
  const { hash, search, pathname, state: navigationState } = useLocation();
  const { redirectToApp, loginWithOtp, login, state } = useLoginService();
  const { track, trackFail, trackSuccess } = useMixpanel({ screen: 'login' });
  const { t } = useTranslation(DEFAULT_NS, { keyPrefix: 'auth.login' });
  const { myProfile } = useLoggedUser();
  const navigate = useNavigate();
  const isPrimaryTheme = useIsPrimaryTheme();
  const [isMobileQP] = useQueryParam<string>('m');
  const urlParams = queryString.parse(hash);
  const searchParams = queryString.parse(search);

  const idToken = urlParams?.id_token as string;
  const returnTo =
    (urlParams?.state as string) ??
    (navigationState as { redirectTo?: string } | null)?.redirectTo ??
    (searchParams?.returnTo as string);

  // Cognito redirects back to /login#error=...&error_description=... when the
  // SAML round-trip with the IdP fails (e.g. ACS URL/audience mismatch, missing
  // attributes). Surface those errors so they're not silently swallowed.
  const ssoError = (urlParams?.error || searchParams?.error) as string | undefined;
  const ssoErrorDescription = (urlParams?.error_description || searchParams?.error_description) as
    | string
    | undefined;

  const userUuid = searchParams?.uuid as string;
  const otp = searchParams?.otp as string;
  const username = searchParams?.username as string;
  const password = searchParams?.password as string;

  const bridgeCodeChallenge = searchParams?.[BRIDGE_PARAM_CODE_CHALLENGE] as string | undefined;
  const bridgeState = searchParams?.[BRIDGE_PARAM_STATE] as string | undefined;
  const bridgeRedirectUri = searchParams?.[BRIDGE_PARAM_REDIRECT_URI] as string | undefined;
  const bridgeParamsFromUrl: IClinicianBridgeParams | undefined = useMemo(
    () =>
      bridgeCodeChallenge && bridgeState && bridgeRedirectUri
        ? { codeChallenge: bridgeCodeChallenge, state: bridgeState, redirectUri: bridgeRedirectUri }
        : undefined,
    [bridgeCodeChallenge, bridgeState, bridgeRedirectUri],
  );

  const prepareLoginFlow = () => {
    if (isMobileQP && !bridgeParamsFromUrl) {
      localStorage[mobileLoginCookieKey] = isMobileQP;
    } else {
      delete localStorage[mobileLoginCookieKey];
    }

    if (bridgeParamsFromUrl) {
      storeClinicianBridgeParams(bridgeParamsFromUrl);
    } else {
      localStorage.removeItem(clinicianBridgeParamsStorageKey);
      if (bridgeCodeChallenge) {
        trackFail('clinician_bridge_v2_missing_params', {
          missingState: !bridgeState,
          missingRedirectUri: !bridgeRedirectUri,
        });
      }
    }

    cookies.remove('id_token');
    cookies.remove('access_token');
  };

  useEffect(() => {
    if (idToken) {
      track('received_oauth_login_token');
      redirectToApp({ idToken, returnTo });
    } else {
      prepareLoginFlow();
    }
  }, [idToken]);

  useEffect(() => {
    if (ssoError === LOGIN_REFUSED_ERROR) {
      toastError({
        content: (
          <>
            {t('loginRefused.title')}
            <br />
            {t('loginRefused.body')}
          </>
        ),
      });
      navigate(pathname, { replace: true });
    } else if (ssoError || ssoErrorDescription) {
      trackFail('cognito_sso', { error: ssoError, error_description: ssoErrorDescription });
      toastError({
        content: (
          <>
            {t('ssoFailed.title')}
            <br />
            {t('ssoFailed.body')}
          </>
        ),
      });
      navigate(pathname, { replace: true });
    }
  }, [ssoError, ssoErrorDescription]);

  useEffect(() => {
    const load = async () => {
      const mixpanel_action = 'login_with_otp';
      try {
        track(mixpanel_action);
        await loginWithOtp({ uuid: userUuid, otp });
        trackSuccess(mixpanel_action);
      } catch {
        toastError({ content: 'Failed to login using the OTP' });
        trackFail(mixpanel_action);
      }
    };
    if (userUuid && otp) {
      load();
    }
  }, [userUuid, otp]);

  useEffect(() => {
    const load = async () => {
      const mixpanel_action = 'auto_login_with_username_password';
      try {
        track(mixpanel_action);
        await login({ username, password });
      } catch {
        toastError({ content: 'Failed to login using username and password' });
        trackFail(mixpanel_action);
      }
    };
    if (username && password && !state.errorMessage) {
      load();
    }
  }, [username, password, state.errorMessage]);

  useEffect(() => {
    if (
      !myProfile?.uuid ||
      isMobileQP ||
      bridgeParamsFromUrl ||
      state.isResolvingLogin ||
      state.profileStepOnComplete
    ) {
      return undefined;
    }

    const controller = new AbortController();
    resumeSessionAtReturnTo(navigate, returnTo, controller.signal).then(({ isResumed }) => {
      if (!isResumed && !controller.signal.aborted) {
        trackFail('resume_session_at_return_to');
      }
    });
    return () => controller.abort();
  }, [
    myProfile?.uuid,
    isMobileQP,
    bridgeParamsFromUrl,
    state.isResolvingLogin,
    state.profileStepOnComplete,
    returnTo,
  ]);

  if (idToken) {
    return null;
  }

  return (
    <MixpanelProvider mixpanel={{ screen: 'login' }}>
      <LoadingPlaceholder
        isLoading={state.isLoginingWithOtp || (!!(username && password) && !state.errorMessage)}
      >
        <div
          className={classNames(styles.root, {
            [styles.primaryP3Main]: isPrimaryTheme,
          })}
        >
          <div className={classNames('flex', styles.content)}>
            <div
              className={classNames(
                'flex items-center justify-center w-1/2 bg-primary-m3 p-4',
                styles.illustration,
              )}
            >
              <img src={BoxesIllustration} alt="Login illustration" role="img" />
            </div>
            <div
              className={classNames(
                'flex items-center justify-center w-1/2 bg-neutral-m5 p-4',
                styles.formWrapper,
              )}
            >
              <div
                className={classNames(styles.formContainer, 'rounded-4 w-[33rem]', {
                  [styles.isTablet]: isMobile && isTablet,
                })}
              >
                <div className={classNames(styles.padding, 'p-16')}>
                  <div className="flex flex-col gap-6 items-center">
                    <div className="flex items-center w-full">
                      <BackButton />
                      <div className="mx-auto">
                        <LoginHeader />
                      </div>
                    </div>
                    <LoginContent />
                    <Challenges />
                    <ForgotPasswordContent />
                    <LoginWithSsoContent />
                    <WelcomeScreen />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </LoadingPlaceholder>
    </MixpanelProvider>
  );
}
export default observer(Login);
